import { useState, useEffect, useCallback } from "react";
import { apiFetch } from "../utils/api.js";

/**
 * ANA-2: Admin Reporting Dashboard.
 *
 * Near real-time cross-persona metrics with an adjustable time range.
 * Default: last 7 days.
 *
 * Sections:
 *  - User Funnel (signup → engagement depth → CP/XP earners → redeemers)
 *  - Question Metrics (total, quality, cache hit rate)
 *  - Demographics (region/location participation ranking)
 */
export function AdminReportingDashboard() {
  const [days, setDays] = useState(7);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastFetched, setLastFetched] = useState(null);

  const fetchReport = useCallback(async (d) => {
    setLoading(true);
    setError(null);
    try {
      const result = await apiFetch(`/api/admin/reporting?days=${d}`);
      setData(result);
      setLastFetched(new Date());
    } catch (err) {
      setError(err?.message ?? "Failed to load report");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchReport(days); }, [days, fetchReport]);

  const S = {
    page: { padding: "24px 28px", maxWidth: 1100 },
    header: { display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 24, flexWrap: "wrap", gap: 12 },
    title: { fontSize: 22, fontWeight: 900, color: "#f1f5f9", fontFamily: "Georgia,serif", margin: 0 },
    subtitle: { fontSize: 12, color: "#64748b", marginTop: 3 },
    rangeRow: { display: "flex", gap: 6, alignItems: "center" },
    rangeBtn: (active) => ({
      padding: "6px 12px", borderRadius: 7,
      background: active ? "#1e3a5f" : "#0f1623",
      border: `1px solid ${active ? "#3b82f6" : "#1e2d45"}`,
      color: active ? "#60a5fa" : "#64748b",
      fontSize: 12, cursor: "pointer", fontFamily: "inherit", fontWeight: active ? 700 : 400,
    }),
    section: { marginBottom: 28 },
    sectionTitle: { fontSize: 11, fontWeight: 700, color: "#64748b", textTransform: "uppercase", letterSpacing: "0.1em", marginBottom: 12 },
    grid: (cols) => ({ display: "grid", gridTemplateColumns: `repeat(${cols}, 1fr)`, gap: 12 }),
    card: { background: "#0f1623", border: "1px solid #1e2d45", borderRadius: 12, padding: "16px 18px" },
    statVal: { fontSize: 26, fontWeight: 900, color: "#60a5fa", lineHeight: 1 },
    statLabel: { fontSize: 10, color: "#64748b", textTransform: "uppercase", letterSpacing: "0.08em", marginTop: 5 },
    statSub: { fontSize: 11, color: "#475569", marginTop: 4 },
    table: { width: "100%", borderCollapse: "collapse", fontSize: 12 },
    th: { padding: "8px 12px", textAlign: "left", color: "#64748b", fontWeight: 600, borderBottom: "1px solid #1e2d45", fontSize: 11 },
    td: { padding: "8px 12px", color: "#e2e8f0", borderBottom: "1px solid #0f1623" },
    tdRight: { padding: "8px 12px", color: "#60a5fa", fontWeight: 700, textAlign: "right", borderBottom: "1px solid #0f1623" },
    refreshBtn: { padding: "6px 14px", background: "none", border: "1px solid #1e2d45", borderRadius: 7, color: "#64748b", fontSize: 12, cursor: "pointer", fontFamily: "inherit" },
    errorBox: { background: "#7c2d1211", border: "1px solid #ef444433", borderRadius: 10, padding: "14px 18px", color: "#ef4444", fontSize: 13 },
    skeleton: { height: 60, background: "#0f1623", borderRadius: 10, animation: "pulse 1.5s ease infinite" },
  };

  const RANGES = [
    { label: "24h", days: 1 },
    { label: "7d",  days: 7 },
    { label: "30d", days: 30 },
    { label: "90d", days: 90 },
  ];

  const f = data;

  return (
    <div style={S.page}>
      <div style={S.header}>
        <div>
          <h1 style={S.title}>Platform Report</h1>
          <p style={S.subtitle}>
            {lastFetched
              ? `Last updated ${lastFetched.toLocaleTimeString()}`
              : "Loading…"}
          </p>
        </div>
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          <div style={S.rangeRow}>
            {RANGES.map(r => (
              <button key={r.days} style={S.rangeBtn(days === r.days)} onClick={() => setDays(r.days)}>
                {r.label}
              </button>
            ))}
          </div>
          <button style={S.refreshBtn} onClick={() => fetchReport(days)} disabled={loading}>
            {loading ? "…" : "↻ Refresh"}
          </button>
        </div>
      </div>

      {error && <div style={S.errorBox}>{error}</div>}

      {loading && !f && (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(4,1fr)", gap: 12 }}>
          {Array.from({ length: 8 }).map((_, i) => <div key={i} style={S.skeleton} />)}
        </div>
      )}

      {f && (
        <>
          {/* ── User Funnel ───────────────────────────────────────── */}
          <div style={S.section}>
            <div style={S.sectionTitle}>User Funnel</div>
            <div style={S.grid(4)}>
              <StatCard val={f.userFunnel.signedUp} label="Signed Up" sub="new voters in period" s={S} />
              <StatCard val={f.userFunnel.activeVoters} label="Active Voters" sub="sent ≥1 message" s={S} />
              <StatCard val={f.userFunnel.sent1Question} label="Sent 1 Question" sub="unique voters" s={S} />
              <StatCard val={f.userFunnel.sent3PlusQuestions} label="Sent 3+ Questions" sub="deeper engagement" s={S} />
              <StatCard val={f.userFunnel.reacted} label="Reacted" sub="liked / disliked / flagged" s={S}
                detail={`👍 ${f.userFunnel.reactionBreakdown.liked}  👎 ${f.userFunnel.reactionBreakdown.disliked}  🚩 ${f.userFunnel.reactionBreakdown.flagged}`} />
              <StatCard val={f.userFunnel.cpEarners} label="Earned CP" sub="quality questions" s={S} />
              <StatCard val={f.userFunnel.xpEarners} label="Earned XP" sub="played trivia" s={S} />
              <StatCard val={f.userFunnel.redemptionAttempts} label="Redemptions" sub="reward attempts" s={S} />
            </div>
          </div>

          {/* ── Question Metrics ─────────────────────────────────── */}
          <div style={S.section}>
            <div style={S.sectionTitle}>Question Metrics</div>
            <div style={S.grid(4)}>
              <StatCard val={f.questionMetrics.totalQuestions} label="Total Questions" sub="all user messages" s={S} />
              <StatCard val={f.questionMetrics.qualityQuestions} label="Quality Questions" sub="CP score ≥ 1" s={S} />
              <StatCard val={f.questionMetrics.cacheHits} label="Cache Hits" sub="served from cache" s={S} />
              <StatCard val={`${f.questionMetrics.cacheHitRate}%`} label="Cache Hit Rate" sub="cache / total questions" s={S} color="#22c55e" />
            </div>
          </div>

          {/* ── Demographics ─────────────────────────────────────── */}
          <div style={S.section}>
            <div style={S.sectionTitle}>Demographics — Region Participation Ranking</div>
            <div style={{ background: "#0f1623", border: "1px solid #1e2d45", borderRadius: 12, overflow: "hidden" }}>
              {f.demographics.regionRanking.length === 0 ? (
                <div style={{ padding: "20px 18px", color: "#475569", fontSize: 13 }}>No regional data for this period.</div>
              ) : (
                <table style={S.table}>
                  <thead>
                    <tr>
                      <th style={S.th}>#</th>
                      <th style={S.th}>State / Region</th>
                      <th style={S.th}>Country</th>
                      <th style={{ ...S.th, textAlign: "right" }}>New Voters</th>
                    </tr>
                  </thead>
                  <tbody>
                    {f.demographics.regionRanking.map((r, i) => (
                      <tr key={`${r.country}:${r.state}`}>
                        <td style={{ ...S.td, color: "#475569", width: 36 }}>{i + 1}</td>
                        <td style={S.td}>{r.state}</td>
                        <td style={{ ...S.td, color: "#64748b" }}>{r.country}</td>
                        <td style={S.tdRight}>{r.voters}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}

function StatCard({ val, label, sub, detail, s, color = "#60a5fa" }) {
  return (
    <div style={s.card}>
      <div style={{ ...s.statVal, color }}>{val}</div>
      <div style={s.statLabel}>{label}</div>
      {sub && <div style={s.statSub}>{sub}</div>}
      {detail && <div style={{ ...s.statSub, color: "#94a3b8", marginTop: 6 }}>{detail}</div>}
    </div>
  );
}
